import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductImage from '../components/ProductImage';
import ProductDetailTabContent, { ProductDetailSidebarContent } from '../components/ProductDetailTabContent';
import API_BASE_URL from '../config/apiConfig';
import { DEFAULT_PRODUCT_IMAGE } from '../utils/productImage';
import { sanitizeFeatureHtml } from '../utils/sanitizeHtml';
import {
    getDetailTabTitle,
    getSidebarTabs,
    getVisibleDetailTabs,
} from '../utils/productDetailTabs';

const ProductDetail = () => {
    const navigate = useNavigate();
    const { addToCart, clearCart } = useCart();
    const { isAuthenticated } = useAuth();
    const { productId } = useParams();
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('');
    const [isZooming, setIsZooming] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [cartFeedback, setCartFeedback] = useState('');
    const imageContainerRef = useRef(null);
    const thumbnailsRef = useRef(null);
    const thumbnailButtonRefs = useRef([]);
    const cartFeedbackTimerRef = useRef(null);
    const ZOOM_LEVEL = 2.5;

    const productImages = useMemo(() => {
        const urls = (product?.Images || []).map((image) => image.ImageUrl).filter(Boolean);
        return urls.length ? urls : [DEFAULT_PRODUCT_IMAGE];
    }, [product?.Images]);
    const selectedImage = productImages[activeImageIndex] || DEFAULT_PRODUCT_IMAGE;

    const visibleTabs = useMemo(
        () => getVisibleDetailTabs(product?.DetailTabs),
        [product?.DetailTabs],
    );

    const sidebarTabs = useMemo(
        () => getSidebarTabs(product?.DetailTabs),
        [product?.DetailTabs],
    );

    useEffect(() => {
        const fetchProduct = async () => {
            const parsedProductId = productId == null || productId === '' ? null : Number(productId);
            const isInvalidProductId = parsedProductId === null || !Number.isFinite(parsedProductId);

            if (isInvalidProductId) {
                setProduct(null);
                setErrorMessage('Invalid product ID.');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setErrorMessage('');

                const query = new URLSearchParams({ ProductId: String(parsedProductId) }).toString();
                const response = await fetch(`${API_BASE_URL}/product/getById?${query}`);
                const result = await response.json();

                if (response.ok && result?.success && result.data) {
                    setProduct(result.data);
                    setActiveImageIndex(0);
                    setQuantity(1);
                } else {
                    setProduct(null);
                    setErrorMessage('Product not found.');
                }
            } catch (error) {
                setProduct(null);
                setErrorMessage('Failed to load product detail.');
                console.error('Failed to load product detail:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProduct();
    }, [productId]);

    useEffect(() => {
        if (!product) return;
        const tabs = getVisibleDetailTabs(product.DetailTabs);
        if (tabs.length > 0) {
            setActiveTab((current) => (
                tabs.some((tab) => tab.TabKey === current) ? current : tabs[0].TabKey
            ));
        } else {
            setActiveTab('');
        }
    }, [product]);

    useEffect(() => {
        const activeThumb = thumbnailButtonRefs.current[activeImageIndex];
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [activeImageIndex, productImages.length]);

    useEffect(() => () => {
        if (cartFeedbackTimerRef.current) {
            clearTimeout(cartFeedbackTimerRef.current);
        }
    }, []);

    const showCartFeedback = () => {
        setCartFeedback('added');
        if (cartFeedbackTimerRef.current) {
            clearTimeout(cartFeedbackTimerRef.current);
        }
        cartFeedbackTimerRef.current = setTimeout(() => {
            setCartFeedback('');
            cartFeedbackTimerRef.current = null;
        }, 2000);
    };

    const handleMouseMove = (e) => {
        if (!imageContainerRef.current) return;
        const rect = imageContainerRef.current.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
        setZoomPosition({ x, y });
    };

    const handleMouseEnter = () => {
        setIsZooming(true);
    };

    const handleMouseLeave = () => {
        setIsZooming(false);
        setZoomPosition({ x: 50, y: 50 });
    };

    const handleQuantityDecrease = (event) => {
        event.preventDefault();
        setQuantity((previous) => Math.max(1, previous - 1));
    };

    const handleQuantityIncrease = (event) => {
        event.preventDefault();
        setQuantity((previous) => previous + 1);
    };

    const handlePrevImage = () => {
        if (!productImages.length) return;
        setActiveImageIndex((previous) => (previous - 1 + productImages.length) % productImages.length);
    };

    const handleNextImage = () => {
        if (!productImages.length) return;
        setActiveImageIndex((previous) => (previous + 1) % productImages.length);
    };

    const handleAddToCart = () => {
        if (!product) {
            return;
        }

        addToCart(product, quantity);
        showCartFeedback();
    };

    const handleBuyNow = () => {
        if (!product) {
            return;
        }

        if (!isAuthenticated) {
            clearCart();
        }

        addToCart(product, quantity);
        navigate('/shop-cart');
    };

    if (isLoading) {
        return (
            <div className="container mb-30">
                <div className="row">
                    <div className="col-xl-10 col-lg-12 m-auto">
                        <div className="product-detail accordion-detail">
                            <div className="row mb-50 mt-30">
                                <div className="col-12">Loading product details...</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (errorMessage || !product) {
        return (
            <div className="container mb-30">
                <div className="row">
                    <div className="col-xl-10 col-lg-12 m-auto">
                        <div className="product-detail accordion-detail">
                            <div className="row mb-50 mt-30">
                                <div className="col-12">{errorMessage || 'Product not available.'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mb-30">
            <div className="row">
                <div className="col-xl-10 col-lg-12 m-auto">
                    <div className="product-detail accordion-detail">
                        <div className="row mb-50 mt-30">
                            <div className="col-md-6 col-sm-12 col-xs-12 mb-md-0 mb-sm-5">
                                <div className="detail-gallery">
                                    <span className="zoom-icon"><i className="fi-rs-search"></i></span>
                                    <div className="product-image-slider">
                                        <figure
                                            className={`border-radius-10 product-image-figure ${isZooming ? 'is-zooming' : ''}`}
                                            ref={imageContainerRef}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseLeave={handleMouseLeave}
                                            onMouseMove={handleMouseMove}
                                        >
                                            <ProductImage
                                                src={selectedImage}
                                                alt={product.ProductName || 'product'}
                                                className={`product-main-image ${isZooming ? 'is-hidden' : ''}`}
                                            />
                                            {isZooming && selectedImage && (
                                                <div
                                                    className="image-zoom-overlay"
                                                    style={{
                                                        backgroundImage: `url(${selectedImage})`,
                                                        backgroundSize: `${ZOOM_LEVEL * 100}%`,
                                                        backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                                                    }}
                                                />
                                            )}
                                        </figure>
                                    </div>
                                    <div className="product-thumbnails-nav">
                                        <button
                                            type="button"
                                            className="product-slider-arrow product-thumb-arrow"
                                            onClick={handlePrevImage}
                                            aria-label="Show previous thumbnail"
                                        >
                                            <i className="fi-rs-angle-small-left"></i>
                                        </button>
                                        <div className="slider-nav-thumbnails product-thumbnails-wrapper" ref={thumbnailsRef}>
                                            {productImages.map((image, index) => (
                                                <button
                                                    key={`${image}-${index}`}
                                                    type="button"
                                                    ref={(element) => {
                                                        thumbnailButtonRefs.current[index] = element;
                                                    }}
                                                    className={`thumbnail-item ${activeImageIndex === index ? 'is-active' : ''}`}
                                                    onClick={() => setActiveImageIndex(index)}
                                                    aria-label={`Show product image ${index + 1}`}
                                                >
                                                    <ProductImage src={image} alt={product.ProductName || 'product'} className="product-thumbnail-image" />
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            className="product-slider-arrow product-thumb-arrow"
                                            onClick={handleNextImage}
                                            aria-label="Show next thumbnail"
                                        >
                                            <i className="fi-rs-angle-small-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 col-sm-12 col-xs-12">
                                <div className="detail-info pr-30 pl-30">
                                    <h4 className="title-detail">{product.ProductName}</h4>

                                    <div className="clearfix product-price-cover">
                                        <div className="product-price primary-color float-left">
                                            <i className="fa fa-inr fs-20" aria-hidden="true"></i>
                                            <span className="current-price text-brand">{product.Price}</span>
                                        </div>
                                    </div>
                                    {(product.Features || []).length > 0 ? (
                                        <div className="short-desc mb-30">
                                            {(product.Features || []).map((feature) => (
                                                <div
                                                    key={feature.FeatureId || feature.FeatureText}
                                                    className="product-feature-html font-lg"
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeFeatureHtml(feature.FeatureText),
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ) : null}
                                    <div className="detail-extralink mb-50">
                                        <div className="detail-qty border radius">
                                            <a href="/" className="qty-down" onClick={handleQuantityDecrease}><i className="fi-rs-angle-small-down"></i></a>
                                            <input type="text" name="quantity" className="qty-val" value={quantity} min="1" readOnly />
                                            <a href="/" className="qty-up" onClick={handleQuantityIncrease}><i className="fi-rs-angle-small-up"></i></a>
                                        </div>
                                        <div className="product-extra-link2">
                                            <button type="button" className="button button-add-to-cart" onClick={handleAddToCart}>
                                                <i className="fi-rs-shopping-cart"></i>Add to Cart
                                            </button>
                                            <button type="button" className="button button-buy-now ml-10" onClick={handleBuyNow}>
                                                Buy Now
                                            </button>
                                        </div>
                                        {cartFeedback === 'added' ? (
                                            <p className="text-success mt-10 mb-0">Added to cart</p>
                                        ) : null}
                                    </div>
                                    {sidebarTabs.map((tab) => (
                                        <div className="font-xm" key={tab.TabKey}>
                                            <h4 className="title-detail mb-10">{getDetailTabTitle(tab)}</h4>
                                            <ProductDetailSidebarContent tab={tab} product={product} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {visibleTabs.length > 0 ? (
                            <div className="product-info">
                                <div className="tab-style3">
                                    <ul className="nav nav-tabs text-uppercase">
                                        {visibleTabs.map((tab) => (
                                            <li className="nav-item" key={tab.TabKey}>
                                                <a
                                                    className={`nav-link ${activeTab === tab.TabKey ? 'active' : ''}`}
                                                    id={`${tab.TabKey}-tab`}
                                                    href={`#${tab.TabKey}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setActiveTab(tab.TabKey);
                                                    }}
                                                >
                                                    {getDetailTabTitle(tab)}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="tab-content shop_info_tab entry-main-content">
                                        {visibleTabs.map((tab) => (
                                            <div
                                                key={tab.TabKey}
                                                className={`tab-pane fade ${activeTab === tab.TabKey ? 'show active' : ''}`}
                                                id={tab.TabKey}
                                            >
                                                <ProductDetailTabContent tab={tab} product={product} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
